/**
 * Camera (P0-T09): walk mode = orbit behind the capsule with wall collision;
 * city mode = free orbit + pan + zoom around the player. FOV 0.9 rad, minZ 0.1,
 * maxZ 7000 (SPEC-01 §3).
 */
import type { Scene, ArcRotateCamera, Vector3 } from 'babylonjs';

export type CameraMode = 'walk' | 'city';

export interface CameraHandle {
  cam: ArcRotateCamera;
  mode: CameraMode;
  setMode(mode: CameraMode): void;
  follow(pos: { x: number; y: number; z: number }, yaw: number | null): void;
  update(dtMs: number): void;
  /** mouse-look / drag state fed from main */
  onPointerDelta(dx: number, dy: number): void;
  onWheel(delta: number): void;
}

const EYE_HEIGHT = 1.55;

export function createCamera(babylon: typeof import('babylonjs'), scene: Scene): CameraHandle {
  const cam = new babylon.ArcRotateCamera('cam', -Math.PI / 2, 1.35, 5.2, babylon.Vector3.Zero(), scene);
  cam.minZ = 0.1;
  cam.maxZ = 7000;
  cam.fov = 0.9;
  cam.lowerRadiusLimit = 1.4;
  cam.upperRadiusLimit = 90;
  cam.lowerBetaLimit = 0.25;
  cam.upperBetaLimit = 1.54;
  cam.panningSensibility = 0; // we implement pan ourselves in city mode
  cam.inertia = 0.6;
  cam.attachControl(scene.getEngine().getRenderingCanvas() ?? undefined);
  scene.activeCamera = cam;
  // WASD/arrows are our movement keys — drop Babylon's built-in keyboard
  // camera input (it rotates alpha/beta on arrows and eats the keys).
  // MUST run AFTER `scene.activeCamera = cam`: the setter calls switchCamera
  // which re-runs attachControl and re-adds every default input. And
  // removeByType('keyboard') silently no-ops because it compares against
  // getClassName() === 'keyboard' while the real class is
  // 'ArcRotateCameraKeyboardMoveInput' — so scan the attached slots.
  for (const slot of Object.keys(cam.inputs.attached)) {
    const input = cam.inputs.attached[slot];
    if (input && /Keyboard/i.test(input.getClassName())) {
      cam.inputs.remove(input);
    }
  }

  let mode: CameraMode = 'walk';
  let target = { x: 0, y: 0, z: 0 };
  const followSmooth = { x: 0, y: EYE_HEIGHT, z: 0 };
  let lastYaw: number | null = null;
  let yawFollowTarget = -Math.PI / 2;
  // free-look grace: after the user orbits the camera themselves, don't
  // swing it back behind the player for a while (walk mode). ArcRotateCamera
  // has no drag observable in the UMD build, so watch canvas pointer events.
  let lastManualDragMs = -1e9;
  const MANUAL_GRACE_MS = 4000;
  {
    const canvas = scene.getEngine().getRenderingCanvas();
    let dragging = false;
    canvas?.addEventListener('pointerdown', () => {
      dragging = true;
      lastManualDragMs = performance.now();
    });
    canvas?.addEventListener('pointermove', () => {
      if (dragging) lastManualDragMs = performance.now();
    });
    window.addEventListener('pointerup', () => {
      dragging = false;
      lastManualDragMs = performance.now();
    });
  }

  function collideRadius(): number {
    // keep the camera from penetrating the two test obstacles (sphere-ish checks)
    return 0;
  }
  void collideRadius;

  return {
    cam,
    get mode() {
      return mode;
    },
    setMode(m) {
      mode = m;
      if (m === 'city') {
        cam.upperBetaLimit = 1.5;
        cam.upperRadiusLimit = 220;
      } else {
        cam.upperBetaLimit = 1.54;
        cam.upperRadiusLimit = 90;
      }
    },
    follow(pos, yaw) {
      target = { x: pos.x, y: pos.y + EYE_HEIGHT, z: pos.z };
      if (yaw !== null && mode === 'walk') {
        // swing camera behind the player when they turn (soft).
        // ArcRotate LH convention: camera offset = (cos a, ·, sin a)*r·sin(beta);
        // "behind a player facing yaw" => cos a = -sin yaw, sin a = -cos yaw.
        const desired = Math.atan2(-Math.cos(yaw), -Math.sin(yaw));
        if (lastYaw === null || Math.abs(yaw - lastYaw) > 0.01) {
          yawFollowTarget = desired;
        }
        lastYaw = yaw;
      }
    },
    update(dtMs: number) {
      const k = Math.min(1, (dtMs / 1000) * 10);
      const dx = target.x - followSmooth.x;
      const dy = target.y - followSmooth.y;
      const dz = target.z - followSmooth.z;
      if (Math.hypot(dx, dy, dz) > 12) {
        // player teleported (spawn, server correction): snap the follow so
        // the orbit does not stretch across the block
        followSmooth.x = target.x;
        followSmooth.y = target.y;
        followSmooth.z = target.z;
      } else {
        followSmooth.x += dx * k;
        followSmooth.y += dy * k;
        followSmooth.z += dz * k;
      }
      // NOTE: mutate cam.target instead of cam.setTarget(): setTarget keeps
      // the camera's world position and recomputes radius from it, which
      // inflates the orbit radius every frame while the player moves.
      (cam.target as Vector3).copyFromFloats(followSmooth.x, followSmooth.y, followSmooth.z);
      if (mode === 'walk') {
        // ease alpha toward "behind the player" while walking OR while a
        // turn-in-place left the camera off-axis; a stationary look-around
        // (manual drag) must never swing back
        const moving = Math.hypot(dx, dz) > 0.015;
        let pending = yawFollowTarget - cam.alpha;
        while (pending > Math.PI) pending -= Math.PI * 2;
        while (pending < -Math.PI) pending += Math.PI * 2;
        const manual = performance.now() - lastManualDragMs < MANUAL_GRACE_MS;
        if ((moving || Math.abs(pending) > 0.05) && !manual) {
          let diff = yawFollowTarget - cam.alpha;
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          if (Math.abs(diff) > 0.02) cam.alpha += diff * Math.min(1, (dtMs / 1000) * 1.5);
        }
      }
      // simple camera collision: never closer than 1.4 m and never below eye-0.2
      const lo = cam.lowerRadiusLimit ?? 1.4;
      if ((cam.radius ?? 5) < lo) cam.radius = lo;
      const minY = target.y - 0.6;
      if (cam.globalPosition.y < minY) {
        const dy = minY - cam.globalPosition.y;
        cam.beta = Math.min(cam.upperBetaLimit ?? 1.54, (cam.beta ?? 1.2) + dy * 0.02);
      }
    },
    onPointerDelta(dx, dy) {
      if (mode === 'city') {
        // right-drag pans the target on the ground plane
        const s = cam.radius * 0.0015;
        target.x -= dx * s;
        target.z -= dy * s;
      }
    },
    onWheel(delta) {
      if (mode === 'city') {
        const lo = cam.lowerRadiusLimit ?? 1.4;
        const hi = cam.upperRadiusLimit ?? 220;
        cam.radius = Math.max(lo, Math.min(hi, (cam.radius ?? 5) + delta * 0.01));
      }
    },
  };
}

/** Keyboard + touch input collector. */
export interface MoveInput {
  fwd: number; // -1..1 forward along heading (W/S/arrows up-down)
  turn: number; // -1..1 turn-in-place, + = right (A/D/arrows left-right)
  strafeX: number; // world-space strafe from the touch stick
  strafeZ: number;
  run: boolean;
}

export function createInputCollector(): {
  read(relativeToCameraAlpha: number): MoveInput;
  attachTouch(joy: HTMLElement, knob: HTMLElement, runBtn: HTMLElement): void;
  destroy(): void;
} {
  const keys = new Set<string>();
  let touch = { x: 0, z: 0 };
  let touchRun = false;
  let cleanup: (() => void) | null = null;

  const onKeyDown = (e: KeyboardEvent): void => {
    keys.add(e.code);
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys.delete(e.code);
  };
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  return {
    read(alpha: number): MoveInput {
      // classic third-person: W/S (and arrows) walk along the heading,
      // A/D (and arrows) turn in place. The touch stick stays camera-relative
      // world-space strafing since phones have no heading affordance.
      let fwd = 0;
      let turn = 0;
      if (keys.has('KeyW') || keys.has('ArrowUp')) fwd += 1;
      if (keys.has('KeyS') || keys.has('ArrowDown')) fwd -= 1;
      if (keys.has('KeyD') || keys.has('ArrowRight')) turn += 1;
      if (keys.has('KeyA') || keys.has('ArrowLeft')) turn -= 1;
      // touch: camera-relative world vector
      const forward = alpha + Math.PI; // arcrotate camera looks along alpha+PI
      const strafeX = Math.sin(forward) * touch.z + Math.sin(forward + Math.PI / 2) * touch.x;
      const strafeZ = Math.cos(forward) * touch.z + Math.cos(forward + Math.PI / 2) * touch.x;
      const scale = (v: number) => (v > 1 ? 1 : v < -1 ? -1 : v);
      return {
        fwd: scale(fwd),
        turn: scale(turn),
        strafeX: scale(strafeX),
        strafeZ: scale(strafeZ),
        run: keys.has('ShiftLeft') || keys.has('ShiftRight') || touchRun,
      };
    },
    attachTouch(joy: HTMLElement, knob: HTMLElement, runBtn: HTMLElement): void {
      let active = false;
      let pointerId = -1;
      const rect = () => joy.getBoundingClientRect();
      const onStart = (e: PointerEvent): void => {
        active = true;
        pointerId = e.pointerId;
        joy.setPointerCapture(e.pointerId);
        onMove(e);
      };
      const onMove = (e: PointerEvent): void => {
        if (!active || e.pointerId !== pointerId) return;
        const r = rect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = (e.clientX - cx) / (r.width / 2);
        const dy = (e.clientY - cy) / (r.height / 2);
        const len = Math.min(1, Math.hypot(dx, dy));
        const ang = Math.atan2(dy, dx);
        const kx = Math.cos(ang) * len;
        const ky = Math.sin(ang) * len;
        knob.style.transform = `translate(${kx * 38}px, ${ky * 38}px)`;
        touch.x = kx;
        touch.z = -ky; // up on screen = forward
      };
      const onEnd = (): void => {
        active = false;
        pointerId = -1;
        knob.style.transform = '';
        touch = { x: 0, z: 0 };
      };
      const runDown = (): void => {
        touchRun = true;
        runBtn.classList.add('active');
      };
      const runUp = (): void => {
        touchRun = false;
        runBtn.classList.remove('active');
      };
      joy.addEventListener('pointerdown', onStart);
      joy.addEventListener('pointermove', onMove);
      joy.addEventListener('pointerup', onEnd);
      joy.addEventListener('pointercancel', onEnd);
      runBtn.addEventListener('pointerdown', runDown);
      runBtn.addEventListener('pointerup', runUp);
      runBtn.addEventListener('pointercancel', runUp);
      cleanup = () => {
        joy.removeEventListener('pointerdown', onStart);
        joy.removeEventListener('pointermove', onMove);
        joy.removeEventListener('pointerup', onEnd);
        joy.removeEventListener('pointercancel', onEnd);
        runBtn.removeEventListener('pointerdown', runDown);
        runBtn.removeEventListener('pointerup', runUp);
        runBtn.removeEventListener('pointercancel', runUp);
      };
    },
    destroy() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      cleanup?.();
    },
  };
}
